import { useToast } from "@/hooks/use-toast";
import {
  horoscopeService,
  type CreateHoroscopeEntryData,
  type ReviewHoroscopeEntryData,
  type UpdateHoroscopeEntryData,
  type UpdateHoroscopeEntryStatusData,
} from "@/lib/api/horoscope.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const HOROSCOPE_ENTRIES_QUERY_KEY = ["/horoscope/entries"];

interface UseHoroscopeEntriesOptions {
  zodiacSign?: string;
  date?: string;
  status?: string;
  language?: string;
  limit?: number;
  offset?: number;
}

export function useHoroscopeEntries(options: UseHoroscopeEntriesOptions = {}) {
  return useQuery({
    queryKey: [...HOROSCOPE_ENTRIES_QUERY_KEY, options],
    queryFn: () => horoscopeService.getEntries(options),
    retry: false,
  });
}

export function useHoroscopeEntry(id: string | null) {
  return useQuery({
    queryKey: [...HOROSCOPE_ENTRIES_QUERY_KEY, id],
    queryFn: () => horoscopeService.getEntryById(id!),
    enabled: !!id,
    retry: false,
  });
}

export function useCreateHoroscopeEntry() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateHoroscopeEntryData) =>
      horoscopeService.createEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: HOROSCOPE_ENTRIES_QUERY_KEY,
      });
      toast({ title: "Horoscope entry created" });
    },
    onError: (err: Error) => {
      toast({
        title: err.message || "Failed to create entry",
        variant: "destructive",
      });
    },
  });
}

export function useBulkCreateHoroscopeEntries() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (entries: CreateHoroscopeEntryData[]) =>
      horoscopeService.bulkCreateEntries(entries),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: HOROSCOPE_ENTRIES_QUERY_KEY,
      });
      if (response.created > 0) {
        toast({
          title: "Success",
          description: `Successfully mapped ${response.created} horoscope entries.`,
        });
      }
      if (response.updated) {
        toast({
          title: "Translations Added",
          description: `Added new languages to ${response.updated} existing draft entries.`,
        });
      }
      if (response.skipped > 0) {
        toast({
          title: "Duplicates Skipped",
          description: `Skipped ${response.skipped} duplicate entries.`,
        });
      }
      if (response.errors.length > 0) {
        toast({
          variant: "destructive",
          title: "Upload Errors",
          description: `Encountered ${response.errors.length} errors during upload.`,
        });
      }
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to bulk create horoscope entries",
      });
    },
  });
}

export function useUpdateHoroscopeEntry() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateHoroscopeEntryData;
    }) => horoscopeService.updateEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: HOROSCOPE_ENTRIES_QUERY_KEY,
      });
      toast({ title: "Horoscope entry updated" });
    },
    onError: (err: Error) => {
      toast({
        title: err.message || "Failed to update entry",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateHoroscopeEntryStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateHoroscopeEntryStatusData;
    }) => horoscopeService.updateEntryStatus(id, data),
    onSuccess: (_, { data }) => {
      queryClient.invalidateQueries({
        queryKey: HOROSCOPE_ENTRIES_QUERY_KEY,
      });
      toast({
        title:
          data.status === "submitted"
            ? "Submitted for approval"
            : "Horoscope entry status updated",
      });
    },
    onError: (err: Error) => {
      toast({
        title: err.message || "Failed to update entry status",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteHoroscopeEntry() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => horoscopeService.deleteEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: HOROSCOPE_ENTRIES_QUERY_KEY,
      });
      toast({ title: "Horoscope entry deleted" });
    },
    onError: (err: Error) => {
      toast({
        title: err.message || "Failed to delete entry",
        variant: "destructive",
      });
    },
  });
}

export function useReviewHoroscopeEntry() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: ReviewHoroscopeEntryData;
    }) => horoscopeService.reviewEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: HOROSCOPE_ENTRIES_QUERY_KEY,
      });
      toast({ title: "Horoscope entry reviewed" });
    },
    onError: (err: Error) => {
      toast({
        title: err.message || "Failed to review entry",
        variant: "destructive",
      });
    },
  });
}
